import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { format } from 'date-fns';

// ── Tutor Dashboard ───────────────────────────────────────
export function TutorDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tutors/dashboard').then(r => setData(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <DashboardLayout title="Dashboard"><div className="loading-screen"><div className="spinner"/></div></DashboardLayout>;

  const tutor = data?.tutor || {};
  const isPending = tutor.status === 'pending';
  const isApproved = tutor.status === 'approved';

  return (
    <DashboardLayout title="Dashboard" subtitle={`Welcome back, ${user?.first_name}`}>
      {isPending && (
        <div className="alert alert-warning" style={{marginBottom:24}}>
          <span>⏳</span>
          <div><strong>Application under review</strong> — Our team will review your application within 48 hours.</div>
        </div>
      )}
      {tutor.status === 'approved' && (
        <div className="stats-grid">
          {[
            {label:'Total lessons',   value: tutor.total_lessons || 0},
            {label:'Average rating',  value: tutor.average_rating ? `${parseFloat(tutor.average_rating).toFixed(1)} ★` : '—'},
            {label:'Reviews',         value: tutor.total_reviews || 0},
            {label:'Pending payout',  value: user?.currency==='USD' ? `${parseFloat(tutor.payout_balance_usd||0).toFixed(2)}` : `£${parseFloat(tutor.payout_balance_gbp||0).toFixed(2)}`},
          ].map(s=>(
            <div key={s.label} className="stat-card">
              <div className="stat-card-label">{s.label}</div>
              <div className="stat-card-value">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
        {/* Pending bookings */}
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Pending requests</h3>
          {!data?.pendingBookings?.length
            ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No pending requests.</p>
            : data.pendingBookings.map(b=>(
              <div key={b.id} style={{padding:'12px 0',borderBottom:'1px solid var(--ink-50)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:600,fontSize:'0.875rem'}}>{b.parent_name}</div>
                  <div style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{b.subject_name} · {b.total_sessions} session{b.total_sessions>1?'s':''}</div>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-primary btn-sm" onClick={()=>api.post(`/bookings/${b.id}/respond`,{response:'accepted'}).then(()=>window.location.reload())}>Accept</button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>api.post(`/bookings/${b.id}/respond`,{response:'declined'}).then(()=>window.location.reload())}>Decline</button>
                </div>
              </div>
            ))
          }
        </div>

        {/* Upcoming lessons */}
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Upcoming lessons</h3>
          {!data?.upcomingLessons?.length
            ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No upcoming lessons.</p>
            : data.upcomingLessons.map(l=>(
              <div key={l.id} style={{padding:'10px 0',borderBottom:'1px solid var(--ink-50)'}}>
                <div style={{fontWeight:600,fontSize:'0.875rem'}}>{l.subject_name}</div>
                <div style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{l.parent_name} · {format(new Date(l.scheduled_at),'EEE d MMM, HH:mm')}</div>
                {l.meet_link && <a href={l.meet_link} target="_blank" rel="noopener noreferrer" style={{fontSize:'0.75rem',color:'var(--green-600)',fontWeight:600}}>Join lesson →</a>}
              </div>
            ))
          }
        </div>

        {/* Recent reviews */}
        <div className="card" style={{gridColumn:'1/-1'}}>
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Recent reviews</h3>
          {!data?.recentReviews?.length
            ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No reviews yet.</p>
            : data.recentReviews.map((r,i)=>(
              <div key={i} style={{padding:'10px 0',borderBottom:'1px solid var(--ink-50)',display:'flex',gap:12}}>
                <span style={{color:'#f59e0b'}}>{'★'.repeat(r.rating)}</span>
                <div>
                  <p style={{margin:0,fontSize:'0.875rem',color:'var(--ink)'}}>{r.review_text || '(No written review)'}</p>
                  <span style={{fontSize:'0.75rem',color:'var(--ink-400)'}}>{r.parent_name} · {format(new Date(r.created_at),'d MMM yyyy')}</span>
                </div>
              </div>
            ))
          }
        </div>
      </div>
      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </DashboardLayout>
  );
}

// ── Tutor Bookings ────────────────────────────────────────
export function TutorBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    api.get('/bookings/my').then(r=>setBookings(r.data.bookings||[])).finally(()=>setLoading(false));
  }, []);

  const filtered = activeTab === 'all' ? bookings : bookings.filter(b=>b.status===activeTab);

  const statusColor = { confirmed:'badge-green', pending:'badge-yellow', cancelled:'badge-red', completed:'badge-grey', expired:'badge-grey' };

  return (
    <DashboardLayout title="My Bookings">
      <div style={{display:'flex',gap:8,marginBottom:20,borderBottom:'1.5px solid var(--ink-100)',paddingBottom:0}}>
        {['all','pending','confirmed','completed','cancelled'].map(tab=>(
          <button key={tab} onClick={()=>setActiveTab(tab)} style={{
            padding:'10px 16px',border:'none',background:'none',cursor:'pointer',fontWeight:700,fontSize:'0.82rem',
            color:activeTab===tab?'var(--green-700)':'var(--ink-400)',
            borderBottom:activeTab===tab?'2.5px solid var(--green-500)':'2.5px solid transparent',
            textTransform:'capitalize'
          }}>{tab}</button>
        ))}
      </div>
      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : filtered.length === 0 ? <div style={{textAlign:'center',padding:40,color:'var(--ink-400)'}}><p>No {activeTab === 'all' ? '' : activeTab} bookings.</p></div>
        : <div className="table-wrap">
          <table>
            <thead><tr><th>Parent</th><th>Subject</th><th>Sessions</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.map(b=>(
                <tr key={b.id}>
                  <td style={{fontWeight:600}}>{b.parent_name}</td>
                  <td>{b.subject_name} · {b.level_name}</td>
                  <td>{b.sessions_completed}/{b.total_sessions}</td>
                  <td><span className={`badge ${statusColor[b.status]||'badge-grey'}`}>{b.status}</span></td>
                  <td style={{fontSize:'0.8rem',color:'var(--ink-400)'}}>{format(new Date(b.created_at),'d MMM yy')}</td>
                  <td>
                    {b.status==='pending' && (
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn btn-primary btn-sm" onClick={()=>api.post(`/bookings/${b.id}/respond`,{response:'accepted'}).then(()=>window.location.reload())}>Accept</button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>api.post(`/bookings/${b.id}/respond`,{response:'declined'}).then(()=>window.location.reload())}>Decline</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </DashboardLayout>
  );
}

// ── Tutor Messages ────────────────────────────────────────
export function TutorMessages() {
  const { user } = useAuth();
  const [bookings, setBookings]   = useState([]);
  const [selected, setSelected]   = useState(null);
  const [messages, setMessages]   = useState([]);
  const [text, setText]           = useState('');
  const [sending, setSending]     = useState(false);
  const [warning, setWarning]     = useState('');
  const bottomRef = useRef(null);

  useEffect(()=>{
    api.get('/bookings/my').then(r=>setBookings((r.data.bookings||[]).filter(b=>b.status!=='cancelled')));
  },[]);

  useEffect(()=>{
    if (!selected) return;
    const load = () => api.get(`/messages/${selected.id}`).then(r=>setMessages(r.data.messages||[]));
    load();
    const iv = setInterval(load, 5000);
    return ()=>clearInterval(iv);
  },[selected]);

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:'smooth'}); },[messages]);

  const send = async () => {
    if (!text.trim() || !selected) return;
    setSending(true); setWarning('');
    try {
      const r = await api.post(`/messages/${selected.id}`, { content: text });
      setMessages(m=>[...m, r.data.message]);
      setText('');
    } catch(err) {
      setWarning(err.response?.data?.message || 'Failed to send');
    }
    setSending(false);
  };

  return (
    <DashboardLayout title="Messages">
      <div style={{display:'grid',gridTemplateColumns:'280px 1fr',gap:0,height:'calc(100vh - 180px)',border:'1.5px solid var(--ink-100)',borderRadius:12,overflow:'hidden',background:'#fff'}}>
        {/* Thread list */}
        <div style={{borderRight:'1.5px solid var(--ink-100)',overflowY:'auto'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid var(--ink-100)',fontWeight:700,fontSize:'0.82rem',color:'var(--ink-400)',textTransform:'uppercase',letterSpacing:'0.05em'}}>Conversations</div>
          {bookings.length === 0 && <p style={{padding:16,fontSize:'0.875rem',color:'var(--ink-400)'}}>No active bookings.</p>}
          {bookings.map(b=>(
            <div key={b.id} onClick={()=>setSelected(b)} style={{
              padding:'14px 16px',cursor:'pointer',borderBottom:'1px solid var(--ink-50)',
              background: selected?.id===b.id ? 'var(--green-50)' : 'transparent',
              transition:'background 0.15s'
            }}>
              <div style={{fontWeight:700,fontSize:'0.875rem',color: selected?.id===b.id?'var(--green-800)':'var(--ink)'}}>{b.parent_name}</div>
              <div style={{fontSize:'0.75rem',color:'var(--ink-400)'}}>{b.subject_name}</div>
            </div>
          ))}
        </div>

        {/* Message pane */}
        {!selected
          ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',color:'var(--ink-400)',flexDirection:'column',gap:8}}>
              <div style={{fontSize:'2.5rem'}}>💬</div>
              <p style={{margin:0}}>Select a booking to view messages</p>
            </div>
          : <div style={{display:'flex',flexDirection:'column'}}>
              <div style={{padding:'12px 16px',borderBottom:'1px solid var(--ink-100)',fontWeight:700}}>
                {selected.parent_name} — {selected.subject_name}
              </div>
              <div style={{flex:1,overflowY:'auto',padding:16,display:'flex',flexDirection:'column',gap:10}}>
                {messages.map((m,i)=>{
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={i} style={{display:'flex',justifyContent:mine?'flex-end':'flex-start'}}>
                      <div style={{
                        maxWidth:'72%',padding:'10px 14px',borderRadius:mine?'16px 16px 4px 16px':'16px 16px 16px 4px',
                        background:mine?'#22c97f':'var(--ink-50)',color:mine?'#fff':'var(--ink)',
                        fontSize:'0.875rem',lineHeight:1.5
                      }}>
                        <p style={{margin:0}}>{m.content}</p>
                        <div style={{fontSize:'0.68rem',color:mine?'rgba(255,255,255,0.6)':'var(--ink-300)',marginTop:4,textAlign:mine?'right':'left'}}>
                          {format(new Date(m.created_at),'HH:mm')}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef}/>
              </div>
              {warning && <div className="alert alert-warning" style={{margin:'0 12px',fontSize:'0.8rem'}}>{warning}</div>}
              <div style={{padding:12,borderTop:'1px solid var(--ink-100)',display:'flex',gap:8}}>
                <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&send()} placeholder="Type a message… (Google Meet links only)" className="form-input" style={{flex:1}}/>
                <button className="btn btn-primary btn-sm" onClick={send} disabled={sending||!text.trim()}>Send</button>
              </div>
            </div>
        }
      </div>
    </DashboardLayout>
  );
}

// ── Tutor Payouts ────────────────────────────────────────
export function TutorPayouts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    api.get('/tutors/payouts').then(r=>setData(r.data)).finally(()=>setLoading(false));
  },[]);

  const fmt = (v) => user?.currency==='USD' ? `$${parseFloat(v||0).toFixed(2)}` : `£${parseFloat(v||0).toFixed(2)}`;
  const statusColor = {pending:'badge-yellow',processing:'badge-blue',completed:'badge-green',failed:'badge-red',held:'badge-grey'};

  return (
    <DashboardLayout title="Payouts">
      {loading ? <div className="loading-screen"><div className="spinner"/></div> : (
        <>
          <div className="stats-grid" style={{marginBottom:28}}>
            <div className="stat-card">
              <div className="stat-card-label">Pending balance</div>
              <div className="stat-card-value" style={{color:'var(--green-700)'}}>{fmt(user?.currency==='USD'?data?.pendingBalance?.usd:data?.pendingBalance?.gbp)}</div>
              <div className="stat-card-sub">Paid out on the 1st of each month</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Minimum threshold</div>
              <div className="stat-card-value">{user?.currency==='USD'?'$30':'£25'}</div>
              <div className="stat-card-sub">Balance rolls over if below</div>
            </div>
          </div>
          <div className="card">
            <h3 style={{marginBottom:16,fontSize:'1rem'}}>Payout history</h3>
            {!data?.payouts?.length
              ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No payouts yet. Your first payout will be processed at end of month.</p>
              : <div className="table-wrap"><table>
                  <thead><tr><th>Period</th><th>Amount</th><th>Lessons</th><th>Status</th><th>Processed</th></tr></thead>
                  <tbody>
                    {data.payouts.map(p=>(
                      <tr key={p.id}>
                        <td style={{fontSize:'0.8rem'}}>{format(new Date(p.period_start),'d MMM')} – {format(new Date(p.period_end),'d MMM yyyy')}</td>
                        <td style={{fontWeight:700,color:'var(--green-700)'}}>{fmt(p.amount)}</td>
                        <td>{p.lessons_count}</td>
                        <td><span className={`badge ${statusColor[p.status]||'badge-grey'}`}>{p.status}</span></td>
                        <td style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{p.processed_at?format(new Date(p.processed_at),'d MMM yy'):'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
            }
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

// ── Tutor Availability ────────────────────────────────────
export function TutorAvailability() {
  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const TIMES = ['07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'];
  const [slots, setSlots]   = useState([]);
  const [notice, setNotice] = useState(24);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    api.get('/tutors/dashboard').then(r => {
      if (r.data.tutor) {
        setNotice(r.data.tutor.notice_period_hours || 24);
      }
    });
    api.get('/search/tutor/' + 'me').catch(() => {});
    // Load availability from profile
    api.get('/tutors/dashboard').then(r => {
      if (r.data.tutor?.slug) {
        api.get('/search/tutor/' + r.data.tutor.slug).then(pr => {
          if (pr.data.tutor?.availability) {
            setSlots(pr.data.tutor.availability.map(a => ({
              day_of_week: a.day_of_week,
              start_time: a.start_time,
              end_time: a.end_time
            })));
          }
        });
      }
    });
  }, []);

  const isSelected = (day,time) => slots.some(s=>s.day_of_week===day&&s.start_time===time+':00');
  const toggle = (day,time) => {
    const exists = isSelected(day,time);
    const endH = parseInt(time)+1;
    setSlots(exists ? slots.filter(s=>!(s.day_of_week===day&&s.start_time===time+':00'))
      : [...slots, {day_of_week:day,start_time:time+':00',end_time:`${String(endH).padStart(2,'0')}:00:00`}]);
  };

  const save = async () => {
    setSaving(true);
    await api.put('/tutors/availability', { slots, notice_period_hours: notice });
    setSaved(true); setSaving(false);
    setTimeout(()=>setSaved(false),2000);
  };

  return (
    <DashboardLayout title="Availability">
      <div className="card">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <h3 style={{marginBottom:4}}>Weekly availability</h3>
            <p style={{margin:0,fontSize:'0.875rem',color:'var(--ink-400)'}}>Click slots to toggle availability. Parents see these when booking.</p>
          </div>
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <select className="form-select" value={notice} onChange={e=>setNotice(parseInt(e.target.value))} style={{width:'auto'}}>
              <option value={24}>24hr notice</option>
              <option value={48}>48hr notice</option>
              <option value={72}>72hr notice</option>
            </select>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saved?'✓ Saved!':saving?'Saving…':'Save changes'}</button>
          </div>
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{borderCollapse:'separate',borderSpacing:3,minWidth:500}}>
            <thead>
              <tr>
                <th style={{fontSize:'0.72rem',padding:'6px 10px',fontWeight:700,color:'var(--ink-400)',textAlign:'left',minWidth:56}}>Time</th>
                {DAYS.map(d=><th key={d} style={{fontSize:'0.72rem',padding:'6px 8px',fontWeight:700,color:'var(--ink-400)',textAlign:'center',minWidth:60}}>{d.slice(0,3)}</th>)}
              </tr>
            </thead>
            <tbody>
              {TIMES.map(time=>(
                <tr key={time}>
                  <td style={{fontSize:'0.75rem',color:'var(--ink-400)',padding:'3px 10px',whiteSpace:'nowrap'}}>{time}</td>
                  {DAYS.map((_,day)=>(
                    <td key={day} style={{padding:2}}>
                      <button type="button" onClick={()=>toggle(day,time)} style={{
                        width:'100%',height:28,borderRadius:6,border:'none',cursor:'pointer',transition:'all 0.15s',
                        background:isSelected(day,time)?'#22c97f':'#f4faf7',
                        boxShadow:isSelected(day,time)?'0 2px 6px rgba(34,201,127,0.3)':'none'
                      }}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{marginTop:12,fontSize:'0.78rem',color:'var(--ink-300)'}}>{slots.length} slot{slots.length!==1?'s':''} selected</p>
      </div>
    </DashboardLayout>
  );
}

// ── Tutor Profile Edit ────────────────────────────────────
export function TutorProfileEdit() {
  const [bio, setBio]     = useState('');
  const [notice, setNotice] = useState(24);
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    api.get('/tutors/dashboard').then(r => {
      if (r.data.tutor) {
        setBio(r.data.tutor.bio || '');
        setNotice(r.data.tutor.notice_period_hours || 24);
      }
    });
  }, []);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('bio', bio);
      fd.append('notice_period_hours', notice);
      if (avatar) fd.append('avatar', avatar);
      await api.put('/tutors/profile', fd, { headers:{'Content-Type':'multipart/form-data'} });
      setSaved(true); setTimeout(()=>setSaved(false),2000);
    } catch(err) {
      setError(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <DashboardLayout title="Edit Profile">
      <div className="card" style={{maxWidth:640}}>
        {error && <div className="alert alert-error" style={{marginBottom:16}}>{error}</div>}
        {saved && <div className="alert alert-success" style={{marginBottom:16}}>Profile updated successfully!</div>}
        <div className="form-group">
          <label className="form-label">Profile photo</label>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div className="avatar-placeholder avatar-xl" style={{fontSize:'1.5rem'}}>?</div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={()=>document.getElementById('av').click()}>
              {avatar ? `✓ ${avatar.name}` : 'Upload new photo'}
            </button>
            <input id="av" type="file" accept="image/*" style={{display:'none'}} onChange={e=>setAvatar(e.target.files[0])}/>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Bio</label>
          <textarea className="form-textarea" rows={6} value={bio} onChange={e=>setBio(e.target.value)} placeholder="Tell parents about your teaching experience, qualifications, and style..."/>
        </div>
        <div className="form-group">
          <label className="form-label">Booking notice period</label>
          <select className="form-select" value={notice} onChange={e=>setNotice(parseInt(e.target.value))}>
            <option value={24}>24 hours minimum</option>
            <option value={48}>48 hours minimum</option>
            <option value={72}>72 hours minimum</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving…':'Save changes'}</button>
      </div>
    </DashboardLayout>
  );
}

// ── Tutor Settings ────────────────────────────────────────
export function TutorSettings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  return (
    <DashboardLayout title="Settings">
      <div style={{maxWidth:520}}>
        <div className="card" style={{marginBottom:16}}>
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Account details</h3>
          <p style={{fontSize:'0.875rem',color:'var(--ink-500)',margin:0}}>
            <strong>Name:</strong> {user?.first_name} {user?.last_name}<br/>
            <strong>Email:</strong> {user?.email}<br/>
            <strong>Country:</strong> {user?.country === 'GB' ? '🇬🇧 United Kingdom' : '🇺🇸 United States'}<br/>
            <strong>Currency:</strong> {user?.currency}
          </p>
        </div>
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Change password</h3>
          {msg && <div className={`alert ${msg.includes('success')?'alert-success':'alert-error'}`} style={{marginBottom:16}}>{msg}</div>}
          <div className="form-group">
            <label className="form-label">Current password</label>
            <input type="password" className="form-input" value={current} onChange={e=>setCurrent(e.target.value)}/>
          </div>
          <div className="form-group">
            <label className="form-label">New password</label>
            <input type="password" className="form-input" value={newPw} onChange={e=>setNewPw(e.target.value)} minLength={8}/>
          </div>
          <button className="btn btn-primary" disabled={saving} onClick={async()=>{
            setSaving(true);
            try { await api.post('/auth/change-password',{current,newPassword:newPw}); setMsg('Password changed successfully'); }
            catch(err) { setMsg(err.response?.data?.message||'Failed'); }
            setSaving(false);
          }}>{saving?'Saving…':'Change password'}</button>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default TutorDashboard;
